import React, { Component } from 'react'
import { connect } from 'react-redux'

import CreateUserForm from './createUserForm'
import { showCreateUser } from '../refiSelectors'
import {
  createUser,
  findOrCreateGarageEntry,
  toggleCreditUiState,
  toggleProfileUiState,
  openVehicleSectionPreserveProgress,
  openProfileSection,
  openCoverageSection,
} from '../refiActions'
import { onContinueWithoutCredit } from '../credit/creditApplicationChoice'
import { fetchBusinessConfig } from '../../common/config'
import { scrollToHeader } from '../../../helpers/displayFormatHelper'
import {
  selectCurrentRefiVehicle,
  selectCurrentUserBadges,
} from '../../../reducers'
import { Vehicle } from '../../../core/types'
import {
  clearAutosaveMetadata,
  getAutosaveMetadata,
  getNamedForm,
} from '../../../helpers/refiAppAccordionAutosave'

export type Callback<T = void> = (...args: any) => T

export function formatCreateUserFormData(values) {
  const {
    id,
    city,
    email,
    firstName,
    generateEmail,
    lastName,
    line1,
    line2,
    phone,
    state,
    zip,
  } = values

  const address = {
    city,
    line1,
    line2,
    state,
    zip,
  }

  const newValues = {
    address,
    id,
    email,
    firstName,
    generateEmail,
    lastName,
    phone,
  }
  return newValues
}

export interface Props {
  createUser: Callback<Promise<any>>
  fetchBusinessConfig: Callback<Promise<any>>
  findOrCreateGarageEntry: Callback<Promise<any>>
  toggleCreditUiState: Callback
  toggleProfileUiState: Callback
  openVehicleSectionPreserveProgress: Callback
  openProfileSection: Callback
  openCoverageSection: Callback
  show: boolean
  vehicle: Vehicle
  currentUserBadges: string[]
  onContinueWithoutCredit: Callback
  onTrimError?: (user: any, trims: string[]) => void
  productPackageId?: number
}

export class CreateUser extends Component<Props> {
  static displayName = 'CreateUser'

  onSubmit = (values) => {
    const {
      createUser,
      fetchBusinessConfig,
      findOrCreateGarageEntry,
      toggleCreditUiState,
      toggleProfileUiState,
      openVehicleSectionPreserveProgress,
      openProfileSection,
      openCoverageSection,
      onContinueWithoutCredit,
      vehicle,
      currentUserBadges,
      onTrimError,
      productPackageId,
    } = this.props

    const userFormData = formatCreateUserFormData(values)
    const profileForm = getNamedForm('CreateUserForm')
    const { isAutosave, autosaveTarget } = getAutosaveMetadata(profileForm)
    const shouldDeferRatesRefresh =
      isAutosave && autosaveTarget === 'vehicle'

    const applyAutosaveTarget = () => {
      if (!isAutosave) return

      if (autosaveTarget === 'vehicle') {
        openVehicleSectionPreserveProgress()
      } else if (autosaveTarget === 'coverage') {
        openCoverageSection()
      } else {
        openProfileSection()
      }
    }

    return createUser(userFormData)
      .then(
        (user) => {
          return findOrCreateGarageEntry({
            user_id: user.id,
            vehicle_id: vehicle.id,
            category: 'user',
          })
            .then(() => fetchBusinessConfig())
            .then(() => {
              toggleProfileUiState()

              if (shouldDeferRatesRefresh) {
                return user
              }

              const badges = currentUserBadges || []
              if (badges.includes('CALL_CENTER_AGENT')) {
                return onContinueWithoutCredit(user, productPackageId).catch(
                  (error) => {
                    const trims = error?.response?.data?.trims

                    onTrimError?.(user, trims)
                  }
                )
              }

              toggleCreditUiState()
              return user
            })
            .then((result) => {
              applyAutosaveTarget()
              return result
            })
        },
        () => {
          scrollToHeader()
        }
      )
      .finally(() => {
        clearAutosaveMetadata(profileForm)
      })
  }

  render() {
    const { show, vehicle } = this.props

    if (!show) return false

    return (
      <CreateUserForm
        onSubmit={this.onSubmit}
        plate_state={vehicle.plate_state}
      />
    )
  }
}

export const mapStateToProps = (state) => ({
  show: showCreateUser(),
  vehicle: selectCurrentRefiVehicle(state),
  currentUserBadges: selectCurrentUserBadges(state),
})

export default connect(mapStateToProps, {
  createUser,
  fetchBusinessConfig,
  findOrCreateGarageEntry,
  toggleCreditUiState,
  toggleProfileUiState,
  openVehicleSectionPreserveProgress,
  openProfileSection,
  openCoverageSection,
  onContinueWithoutCredit,
})(CreateUser)
